import { useState, useEffect, useCallback } from 'react';
import { llmAPI } from '../api/client';
import { useTranslation } from 'react-i18next';

const LS_KEY_ANALISIS = 'colmena_asistente_analisis';
const LS_KEY_GENERADO = 'colmena_asistente_generado';
const LS_KEY_RESPUESTA = 'colmena_asistente_respuesta';

function formatFecha(iso, lang) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(lang, { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
}

function nextMonthName(mesStr, lang) {
  if (!mesStr) return '';
  const [year, month] = mesStr.split('-').map(Number);
  const nextDate = new Date(year, month, 1);
  return nextDate.toLocaleDateString(lang || 'es', { month: 'long' });
}

function UsageBar({ uso, t, lang }) {
  const { consultas, limite } = uso;
  const porcentaje = Math.min((consultas / limite) * 100, 100);
  const barColor = consultas >= limite ? '#C0392B' : consultas >= limite - 2 ? '#C97820' : '#4A7C59';
  const limitReached = consultas >= limite;

  return (
    <div style={SU.wrapper}>
      <div style={SU.labelRow}>
        <span style={{ fontSize: 12, color: limitReached ? '#C0392B' : '#8B6355', fontWeight: 500 }}>
          {t('asistente.usageLabel', { count: consultas })}
        </span>
        <span style={{ fontSize: 12, color: barColor, fontWeight: 700 }}>
          {consultas}/{limite}
        </span>
      </div>
      <div style={SU.track}>
        <div style={{ ...SU.fill, width: `${porcentaje}%`, background: barColor }} />
      </div>
      {limitReached && (
        <div style={SU.limitMsg}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{t('asistente.limitReached')}</div>
          <div style={{ fontSize: 12, marginTop: 2 }}>
            {t('asistente.limitReset', { mes: nextMonthName(uso.mes, lang) })}
          </div>
        </div>
      )}
    </div>
  );
}

const SU = {
  wrapper: { marginBottom: 12 },
  labelRow: { display: 'flex', justifyContent: 'space-between', marginBottom: 5 },
  track: { height: 6, background: '#F0EDE9', borderRadius: 4, overflow: 'hidden' },
  fill: { height: '100%', borderRadius: 4, transition: 'width 0.4s ease, background 0.4s ease' },
  limitMsg: { marginTop: 8, background: '#FDECEA', color: '#C0392B', borderRadius: 8, padding: '8px 12px', fontSize: 13, lineHeight: 1.5 },
};

export default function AsistenteLLM({ zonaId }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || 'es';

  const [analisis, setAnalisis] = useState(() => {
    try { return localStorage.getItem(LS_KEY_ANALISIS) || ''; } catch { return ''; }
  });
  const [generadoEn, setGeneradoEn] = useState(() => {
    try { return localStorage.getItem(LS_KEY_GENERADO) || ''; } catch { return ''; }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pregunta, setPregunta] = useState('');
  const [respuesta, setRespuesta] = useState(() => {
    try { return localStorage.getItem(LS_KEY_RESPUESTA) || ''; } catch { return ''; }
  });
  const [consultando, setConsultando] = useState(false);
  const [expandido, setExpandido] = useState(() => {
    try { return !!localStorage.getItem(LS_KEY_ANALISIS); } catch { return false; }
  });

  const [historial, setHistorial] = useState([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);
  const [expandidoIds, setExpandidoIds] = useState({});

  const [uso, setUso] = useState({ consultas: 0, limite: 10, mes: '' });

  useEffect(() => {
    try { analisis ? localStorage.setItem(LS_KEY_ANALISIS, analisis) : localStorage.removeItem(LS_KEY_ANALISIS); } catch {}
  }, [analisis]);

  useEffect(() => {
    try { generadoEn ? localStorage.setItem(LS_KEY_GENERADO, generadoEn) : localStorage.removeItem(LS_KEY_GENERADO); } catch {}
  }, [generadoEn]);

  useEffect(() => {
    try { respuesta ? localStorage.setItem(LS_KEY_RESPUESTA, respuesta) : localStorage.removeItem(LS_KEY_RESPUESTA); } catch {}
  }, [respuesta]);

  const cargarHistorial = useCallback(async () => {
    setCargandoHistorial(true);
    try {
      const r = await llmAPI.historial(30);
      setHistorial(r.data);
    } catch {}
    finally { setCargandoHistorial(false); }
  }, []);

  const cargarUso = useCallback(async () => {
    try {
      const r = await llmAPI.uso();
      setUso(r.data);
    } catch {}
  }, []);

  useEffect(() => {
    cargarHistorial();
    cargarUso();
  }, [cargarHistorial, cargarUso]);

  const limitReached = uso.consultas >= uso.limite;

  const analizar = async () => {
    if (limitReached) return;
    setLoading(true); setError(''); setAnalisis(''); setGeneradoEn('');
    try {
      const r = await llmAPI.analisis(zonaId);
      if (r.data.error) {
        setError(r.data.error);
      } else {
        setAnalisis(r.data.analisis);
        setExpandido(true);
        setGeneradoEn(r.data.generado_en || new Date().toISOString());
        cargarHistorial();
        cargarUso();
      }
    } catch (e) {
      if (e.response?.status === 429) {
        cargarUso();
      } else {
        setError(e.response?.data?.detail || t('asistente.connectionError'));
      }
    } finally { setLoading(false); }
  };

  const consultar = async (e) => {
    e.preventDefault();
    if (!pregunta.trim() || limitReached) return;
    setConsultando(true); setRespuesta('');
    try {
      const r = await llmAPI.consulta(pregunta, zonaId);
      setRespuesta(r.data.respuesta || r.data.error || '');
      if (r.data.respuesta) {
        cargarHistorial();
        cargarUso();
      }
    } catch (e) {
      if (e.response?.status === 429) {
        cargarUso();
      } else {
        setRespuesta(e.response?.data?.detail || t('asistente.connectionError'));
      }
    } finally { setConsultando(false); }
  };

  const toggleEntrada = (id) =>
    setExpandidoIds(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div style={S.container}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={S.header}>
        <div>
          <div style={S.titulo}>{t('asistente.title')}</div>
          <div style={S.subtitulo}>{t('asistente.subtitle')}</div>
        </div>
        <button onClick={analizar} disabled={loading || limitReached} style={{
          ...S.analizarBtn,
          opacity: limitReached ? 0.5 : 1,
          cursor: limitReached ? 'not-allowed' : 'pointer',
        }}>
          {loading ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={S.spinner} /> {t('asistente.analyzing')}
            </span>
          ) : t('asistente.analyzeBtn')}
        </button>
      </div>

      {error && (
        <div style={S.errorBox}>
          <strong>{t('asistente.errorLabel')}</strong> {error}
          {error.includes('ANTHROPIC_API_KEY') && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              {t('asistente.apiKeyHint')}
            </div>
          )}
        </div>
      )}

      {analisis && (
        <div style={S.analisisBox}>
          <div style={S.analisisHeader}>
            <div>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{t('asistente.weekAnalysis')}</span>
              {generadoEn && (
                <div style={{ fontSize: 11, color: '#8B6355', marginTop: 2 }}>
                  {formatFecha(generadoEn, lang)}
                </div>
              )}
            </div>
            <button onClick={() => setExpandido(!expandido)} style={S.toggleBtn}>
              {expandido ? t('asistente.less') : t('asistente.more')}
            </button>
          </div>
          {expandido && (
            <div style={S.analisisTexto}>
              {analisis.split('\n').map((line, i) => (
                <p key={i} style={{ margin: '6px 0', lineHeight: 1.6 }}>{line}</p>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={S.consultaBox}>
        <UsageBar uso={uso} t={t} lang={lang} />
        <div style={S.consultaLabel}>{t('asistente.questionLabel')}</div>
        <form onSubmit={consultar} style={{ display: 'flex', gap: 8 }}>
          <input
            value={pregunta}
            onChange={e => setPregunta(e.target.value)}
            placeholder={limitReached ? t('asistente.limitInputDisabled') : t('asistente.questionPlaceholder')}
            style={{ ...S.consultaInput, opacity: limitReached ? 0.6 : 1, cursor: limitReached ? 'not-allowed' : 'text' }}
            disabled={consultando || limitReached}
          />
          <button type="submit" disabled={consultando || !pregunta.trim() || limitReached} style={{
            ...S.consultaBtn,
            opacity: limitReached ? 0.5 : 1,
            cursor: limitReached ? 'not-allowed' : 'pointer',
          }}>
            {consultando ? '...' : '→'}
          </button>
        </form>

        {respuesta && (
          <div style={S.respuestaBox}>
            <div style={{ fontSize: 13, color: '#2C1A0E', lineHeight: 1.7 }}>
              {respuesta.split('\n').map((line, i) => (
                <p key={i} style={{ margin: '4px 0' }}>{line}</p>
              ))}
            </div>
          </div>
        )}
      </div>

      {historial.length > 0 && (
        <div style={S.historialSection}>
          <button
            style={S.historialToggle}
            onClick={() => setMostrarHistorial(v => !v)}
          >
            <span>{t('asistente.historyTitle', { count: historial.length })}</span>
            <span style={{ fontSize: 11 }}>{mostrarHistorial ? t('asistente.historyHide') : t('asistente.historyShow')}</span>
          </button>

          {mostrarHistorial && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {cargandoHistorial ? (
                <div style={{ fontSize: 13, color: '#8B6355', padding: '8px 0' }}>{t('asistente.loading')}</div>
              ) : (
                historial.map(entrada => (
                  <div key={entrada.id} style={S.entradaCard}>
                    <div
                      style={S.entradaHeader}
                      onClick={() => toggleEntrada(entrada.id)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                          background: entrada.tipo === 'analisis' ? '#FFF0D4' : '#EDF7F0',
                          color: entrada.tipo === 'analisis' ? '#C97820' : '#4A7C59',
                          flexShrink: 0,
                        }}>
                          {entrada.tipo === 'analisis' ? t('asistente.typeAnalysis') : t('asistente.typeQuery')}
                        </span>
                        <span style={{ fontSize: 12, color: '#2C1A0E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {entrada.tipo === 'consulta' ? entrada.pregunta : t('asistente.apiaryAnalysis')}
                        </span>
                      </div>
                      <span style={{ fontSize: 11, color: '#8B6355', flexShrink: 0, marginLeft: 8 }}>
                        {formatFecha(entrada.created_at, lang)}
                      </span>
                    </div>
                    {expandidoIds[entrada.id] && (
                      <div style={S.entradaTexto}>
                        {entrada.respuesta.split('\n').map((line, i) => (
                          <p key={i} style={{ margin: '4px 0', lineHeight: 1.6 }}>{line}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const S = {
  container: { background: 'white', borderRadius: 20, padding: '20px 24px', marginTop: 24, boxShadow: '0 2px 16px rgba(44,26,14,0.08)', border: '2px solid rgba(232,146,42,0.2)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 },
  titulo: { fontSize: 16, fontWeight: 600, color: '#2C1A0E' },
  subtitulo: { fontSize: 13, color: '#8B6355', marginTop: 2 },
  analizarBtn: { background: 'linear-gradient(135deg, #E8922A, #C97820)', color: 'white', border: 'none', borderRadius: 12, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  errorBox: { background: '#FDECEA', color: '#C0392B', borderRadius: 10, padding: '12px 14px', marginBottom: 14, fontSize: 13 },
  analisisBox: { background: '#FEFCF8', borderRadius: 12, border: '1px solid #FAE0B0', marginBottom: 16, overflow: 'hidden' },
  analisisHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 14px', borderBottom: '1px solid #FAE0B0' },
  analisisTexto: { padding: '14px 16px', fontSize: 14, color: '#2C1A0E' },
  toggleBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#E8922A', fontSize: 12, fontWeight: 600, flexShrink: 0 },
  consultaBox: { borderTop: '1px solid #F0EDE9', paddingTop: 16 },
  consultaLabel: { fontSize: 13, fontWeight: 600, color: '#8B6355', marginBottom: 8 },
  consultaInput: { flex: 1, padding: '10px 14px', borderRadius: 10, border: '1.5px solid #FAE0B0', fontSize: 14, fontFamily: 'inherit', outline: 'none' },
  consultaBtn: { padding: '10px 16px', background: '#2C1A0E', color: '#F5C47A', border: 'none', borderRadius: 10, fontSize: 16, cursor: 'pointer', fontWeight: 600 },
  respuestaBox: { marginTop: 12, background: '#F8F4EF', borderRadius: 10, padding: '12px 14px' },
  spinner: { display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  historialSection: { marginTop: 20, borderTop: '1px solid #F0EDE9', paddingTop: 16 },
  historialToggle: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: '1px solid #FAE0B0', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#7A6030', fontFamily: 'inherit' },
  entradaCard: { borderRadius: 10, border: '1px solid #F0EDE9', overflow: 'hidden', background: '#FEFEFE' },
  entradaHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', cursor: 'pointer', gap: 8 },
  entradaTexto: { padding: '10px 14px', fontSize: 13, color: '#2C1A0E', borderTop: '1px solid #F0EDE9', background: '#FAFAF8', lineHeight: 1.6 },
};
